import { callSpec } from "./specClient";

/**
 * partner-branding — 14 endpoint(s) from the Admin & Partner Portal API Specification.
 *
 * Each function POSTs to app-backend; the spec method/path in the comment is the
 * OUTBOUND call app-backend makes to partner-engine. Endpoints not yet built on
 * app-backend resolve to a fixture derived from the spec response example — see
 * specClient.js. Promoting one to live is a one-line change in registry.json.
 */

/**
 * §17.1/30.1  Get Branding
 *   FE      POST /app/v1/partner/branding/list
 *   engine  GET /api/v1/partners/{partnerId}/branding   [stubbed]
 *
 * Request: Path parameters: - partnerId (path, string, Required) — Partner key
 *   whose theme is loaded. One of the partner keys, e.g. aldar | snoonu |
 *   travelmax | gotravel. No query, header or body parameters documented.
 *
 * Notes: 200 OK returns a flat Branding object (no data wrapper). Loads the
 *   selected partner's theme when the Branding & Theming page opens or the
 *   partner selector changes; populates the colour pickers, font dropdown
 *   and upload zones. Colours are hex strings. fontFamily must be one of the
 *   supported fonts listed by Branding Options (17.10): Inter | Satoshi | DM
 *   Sans | Plus Jakarta Sans | Poppins | Nunito | Open Sans | Lato. The four
 *   asset URL fields (logoUrl, logoDarkUrl, iconUrl, faviconUrl) hold URLs
 *   returned by Upload Brand Asset (17.3) and are EMPTY STRINGS (not null)
 *   when not set. Request example i
 */
export const partnerBrandingList = (body = {}) => callSpec("partnerBrandingList", body, 'partner');

/**
 * §17.2/30.2  Update Branding
 *   FE      POST /app/v1/partner/branding/update
 *   engine  PUT /api/v1/partners/{partnerId}/branding   [stubbed]
 *
 * Request: Path parameters: - partnerId (path, string, Required) — Partner key
 *   whose theme is being saved. Body (all Required): - primaryColor (body,
 *   string, Yes) — Primary brand colour as a hex value. - secondaryColor
 *   (body, string, Yes) — Secondary brand colour as a hex value. -
 *   accentColor (body, string, Yes) — Accent brand colour as a hex value. -
 *   fontFamily (body, string, Yes) — One of the supported fonts listed by
 *   Branding Options. - logoUrl (body, string, Yes) — URL returned by Upload
 *   Brand Asset for the logoUrl slot; empty string when not set. -
 *   logoDarkUrl (body, string, Yes) — URL returned by
 *
 * Notes: Backs "Save Changes" on the theme area; persists the WHOLE brand theme
 *   (all 8 fields required — this is a full replace, not a patch). 200 OK
 *   returns the saved branding object, same shape as the request. Response
 *   example is marked Derived — the source states it returns the saved
 *   branding object (same shape), so the request body values are echoed
 *   back. Logo/icon/favicon fields must be URLs previously returned by
 *   Upload Brand Asset (17.3); empty string means not set.
 */
export const partnerBrandingUpdate = (body = {}) => callSpec("partnerBrandingUpdate", body, 'partner');

/**
 * §17.3  Upload Brand Asset
 *   FE      POST /app/v1/partner/branding/assets/create
 *   engine  POST /api/v1/partners/{partnerId}/branding/assets   [stubbed]
 *
 * Request: Path parameters: - partnerId (path, string, Required) — Partner key the
 *   asset is uploaded for. Body — multipart/form-data: - field (form-data,
 *   string, Yes) — Which brand asset slot the file fills. One of: logoUrl |
 *   logoDarkUrl | iconUrl | faviconUrl. - file (form-data, binary, Yes) —
 *   The asset file. PNG/SVG/WebP/ICO, max 5MB.
 *
 * Notes: Returns 201 CREATED (not 200). Content-Type is multipart/form-data.
 *   Backs the logo/icon/favicon upload zones. Response: field (echoed slot)
 *   + url (hosted URL). Two-step flow: the client stores the returned url on
 *   the branding object and then persists it via Update Branding (17.2) —
 *   uploading alone does NOT save the theme. Constraints: PNG/SVG/WebP/ICO,
 *   max 5MB.
 */
export const partnerBrandingAssetsCreate = (body = {}) => callSpec("partnerBrandingAssetsCreate", body, 'partner');

/**
 * §30.10  List Document Templates
 *   FE      POST /app/v1/partner/branding/document-templates/list
 *   engine  GET /api/v1/partners/{partnerId}/branding/document-templates   [stubbed]
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. Query: - vertical (query, string, No) — restrict to one
 *   vertical. One of: flight | hotel | activity. Omit to return all
 *   verticals grouped. - includeDeleted (query, boolean, No) — default
 *   false. When false the response excludes soft-deleted rows. Set true to
 *   include them; each returned row then carries deleted, deletedAt and
 *   deletedBy.
 *
 * Notes: 200 OK. Single-vertical shape (?vertical=flight): partnerId, vertical,
 *   data[] of document template objects {id, vertical, name, headerTitle,
 *   headerSubtitle, footerText, footerNote, showLogo, showPartnerName,
 *   showBorder, sections, configured}. The spec enumerates the E-Ticket
 *   section keys explicitly under data[].sections: showSectorDetails,
 *   showPassengerDetails, showFareBreakdown, showImportantNotices. When
 *   includeDeleted=true each row additionally carries deleted (boolean),
 *   deletedAt (ISO-8601 | null) and deletedBy. ALL-VERTICALS SHAPE (vertical
 *   omitted) — grouped arrays, different top level:
 */
export const partnerBrandingDocumentTemplatesList = (body = {}) => callSpec("partnerBrandingDocumentTemplatesList", body, 'partner');

/**
 * §30.11  Create Document Template
 *   FE      POST /app/v1/partner/branding/document-templates/create
 *   engine  POST /api/v1/partners/{partnerId}/branding/document-templates   [stubbed]
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. Body: - vertical (body, string, Yes) — vertical for the new
 *   template. One of: flight | hotel | activity. - name (body, string, Yes)
 *   — document type / display name, e.g. "Credit Note". Unique name per
 *   (partner, vertical) → else 409 DUPLICATE_NAME. - headerTitle (body,
 *   string, No) — document header title, e.g. "CREDIT NOTE". -
 *   headerSubtitle (body, string, No) — document header subtitle. -
 *   footerText (body, string, No) — document footer text. - footerNote
 *   (body, string, No) — additional footer note. - showLogo (bod
 *
 * Notes: 201 CREATED with the full object and a server-assigned id. Response
 *   fields: id, vertical, name, headerTitle, headerSubtitle, footerText,
 *   footerNote, showLogo, showPartnerName, showBorder, sections, configured.
 *   Required: vertical, name only. DEFAULTS: showLogo=true,
 *   showPartnerName=true, showBorder=true, sections={}, configured=false;
 *   text fields default to empty string. ENUM — vertical: flight | hotel |
 *   activity. ERROR: 409 DUPLICATE_NAME when name is not unique per
 *   (partner, vertical). GOTCHA: supports partner-defined CUSTOM document
 *   types, which have no predefined section keys (empty section
 */
export const partnerBrandingDocumentTemplatesCreate = (body = {}) => callSpec("partnerBrandingDocumentTemplatesCreate", body, 'partner');

/**
 * §30.12  Update Document Template
 *   FE      POST /app/v1/partner/branding/document-templates/update
 *   engine  PUT /api/v1/partners/{partnerId}/branding/document-templates/{templateId}   [stubbed]
 *
 * The spec carries templateId in the path; on the POST hop they travel
 * in the body.
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. - templateId (path, string, Required) — the template id,
 *   e.g. aldar-flight-e-ticket. Unknown id → 404 TEMPLATE_NOT_FOUND. Body
 *   (full replacement): - name (body, string, Yes) — document type / display
 *   name. Unique per (partner, vertical) → else 409 DUPLICATE_NAME. -
 *   headerTitle (body, string, No) — document header title. - headerSubtitle
 *   (body, string, No) — document header subtitle. - footerText (body,
 *   string, No) — document footer text. - footerNote (body, string, No) —
 *   additional footer note. - showLogo (body,
 *
 * Notes: 200 OK. Response fields: id, vertical, name, headerTitle,
 *   headerSubtitle, footerText, footerNote, showLogo, showPartnerName,
 *   showBorder, sections, configured. FULL REPLACEMENT (PUT) — header/footer
 *   text, universal display flags and the whole sections toggle map. Saving
 *   ALWAYS sets configured=true. vertical is NOT settable here (returned but
 *   not in the request body). ENUM — vertical (response): flight | hotel |
 *   activity. ERRORS: 404 TEMPLATE_NOT_FOUND for an unknown templateId; 409
 *   DUPLICATE_NAME on a name collision per (partner, vertical); 409 CONFLICT
 *   if the row is soft-deleted (must be resto
 */
export const partnerBrandingDocumentTemplatesUpdate = (body = {}) => callSpec("partnerBrandingDocumentTemplatesUpdate", body, 'partner');

/**
 * §30.13  Delete Document Template
 *   FE      POST /app/v1/partner/branding/document-templates/delete
 *   engine  DELETE /api/v1/partners/{partnerId}/branding/document-templates/{templateId}   [stubbed]
 *
 * The spec carries templateId in the path; on the POST hop they travel
 * in the body.
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. - templateId (path, string, Required) — the template id.
 *   Unknown id → 404 TEMPLATE_NOT_FOUND. No query or body parameters.
 *
 * Notes: 200 OK with the soft-delete envelope: id, deleted (always true),
 *   deletedAt (ISO-8601, null on a live row), deletedBy, restorable. ⚠️
 *   CHANGED FROM SOURCE CONTRACT: the source returns 204 No Content with an
 *   empty body; under the soft-delete policy this returns 200 OK with the
 *   envelope above so the caller receives the deletion stamp. The row is not
 *   removed from storage — the server sets deletedAt and deletedBy, leaves
 *   every other column intact, and excludes the row from list and lookup
 *   responses from that moment on. SOFT-DELETE POLICY: idempotent (repeat
 *   delete returns 200 with the original delet
 */
export const partnerBrandingDocumentTemplatesDelete = (body = {}) => callSpec("partnerBrandingDocumentTemplatesDelete", body, 'partner');

/**
 * §30.3  Upload Logo
 *   FE      POST /app/v1/partner/branding/logos/create
 *   engine  POST /api/v1/partners/{partnerId}/branding/logos/{slot}   [stubbed]
 *
 * The spec carries slot in the path; on the POST hop they travel
 * in the body.
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. - slot (path, string, Required) — logo slot. One of: primary
 *   | dark | icon | favicon. Body (multipart/form-data): - file (form-data,
 *   file, Required) — a single file part. Accepts image/​* (SVG or PNG
 *   recommended, transparent background). Max 2 MB.
 *
 * Notes: 201 CREATED. Response fields: slot, name, url, contentType, sizeBytes
 *   (integer), uploadedAt (ISO-8601). ENUM — slot: primary | dark | icon |
 *   favicon. ERRORS: 400 INVALID_FILE for wrong type / oversize; 400
 *   INVALID_ARGUMENT for an unknown slot. MOCK-UI CAVEAT: the upload returns
 *   a CDN url; the UI currently uses URL.createObjectURL on a local File —
 *   swap that for the returned url. Content-Type must be multipart/form-data
 *   with a single file part. Max 2 MB.
 */
export const partnerBrandingLogosCreate = (body = {}) => callSpec("partnerBrandingLogosCreate", body, 'partner');

/**
 * §30.4  Delete Logo
 *   FE      POST /app/v1/partner/branding/logos/delete
 *   engine  DELETE /api/v1/partners/{partnerId}/branding/logos/{slot}   [stubbed]
 *
 * The spec carries slot in the path; on the POST hop they travel
 * in the body.
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. - slot (path, string, Required) — logo slot to clear. One
 *   of: primary | dark | icon | favicon. No query or body parameters.
 *
 * Notes: Clears the slot back to null in the branding config. Returns 200 OK with
 *   the soft-delete envelope: id, deleted (always true), deletedAt
 *   (ISO-8601, null on a live row), deletedBy, restorable. ⚠️ CHANGED FROM
 *   SOURCE CONTRACT: the source returns 204 No Content with an empty body;
 *   under the soft-delete policy this returns 200 OK with the envelope so
 *   the caller receives the deletion stamp. The row is not removed from
 *   storage. SOFT-DELETE POLICY: Idempotent — deleting an already-deleted
 *   row returns 200 OK with the original deletedAt unchanged (no re-stamp).
 *   A direct GET on a soft-deleted id returns
 */
export const partnerBrandingLogosDelete = (body = {}) => callSpec("partnerBrandingLogosDelete", body, 'partner');

/**
 * §30.5  List Email Templates
 *   FE      POST /app/v1/partner/branding/email-templates/list
 *   engine  GET /api/v1/partners/{partnerId}/branding/email-templates   [stubbed]
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. Query: - vertical (query, string, No) — restrict to one
 *   vertical. One of: flight | hotel | activity. Omit to return all
 *   verticals grouped. - includeDeleted (query, boolean, No) — default
 *   false. When false the response excludes soft-deleted rows. Set true to
 *   include them; each returned row then carries deleted, deletedAt and
 *   deletedBy.
 *
 * Notes: 200 OK. Single-vertical shape (?vertical=flight): partnerId, vertical,
 *   data[] of template objects {id, vertical, name, subject, greeting, body,
 *   ctaText, ctaUrl, footerText, configured}. When includeDeleted=true each
 *   row additionally carries deleted (boolean), deletedAt (ISO-8601 | null)
 *   and deletedBy. ALL-VERTICALS SHAPE (vertical omitted) — a DIFFERENT
 *   top-level shape, grouped arrays with no partnerId/data wrapper: {
 *   "flight": [...], "hotel": [...], "activity": [...] }. ENUM — vertical:
 *   flight | hotel | activity. GOTCHAS: these are the per-vertical email
 *   CONTENT templates, distinct from email
 */
export const partnerBrandingEmailTemplatesList = (body = {}) => callSpec("partnerBrandingEmailTemplatesList", body, 'partner');

/**
 * §30.6  Create Email Template
 *   FE      POST /app/v1/partner/branding/email-templates/create
 *   engine  POST /api/v1/partners/{partnerId}/branding/email-templates   [stubbed]
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. Body: - vertical (body, string, Yes) — vertical for the new
 *   template. One of: flight | hotel | activity. - name (body, string, Yes)
 *   — template display name. Must be unique within (partner, vertical) →
 *   else 409 DUPLICATE_NAME. - subject (body, string, No) — email subject
 *   line; may contain {{...}} variables. Defaults to empty. - greeting
 *   (body, string, No) — greeting line. Defaults to empty. - body (body,
 *   string, No) — email body content. Defaults to empty. - ctaText (body,
 *   string, No) — call-to-action button label
 *
 * Notes: 201 CREATED with the full template object and a server-assigned id.
 *   Response fields: id, vertical, name, subject, greeting, body, ctaText,
 *   ctaUrl, footerText, configured. Required: vertical, name only. All other
 *   content fields optional and default to empty string; configured=false on
 *   create (a stub awaiting content). ENUM — vertical: flight | hotel |
 *   activity. ERROR: 409 DUPLICATE_NAME when name is not unique within
 *   (partner, vertical). NOTE: the body field is literally named `body` (a
 *   body parameter called body). Response example is marked Derived in the
 *   spec, built from the create request bo
 */
export const partnerBrandingEmailTemplatesCreate = (body = {}) => callSpec("partnerBrandingEmailTemplatesCreate", body, 'partner');

/**
 * §30.7  Update Email Template
 *   FE      POST /app/v1/partner/branding/email-templates/update
 *   engine  PUT /api/v1/partners/{partnerId}/branding/email-templates/{templateId}   [stubbed]
 *
 * The spec carries templateId in the path; on the POST hop they travel
 * in the body.
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. - templateId (path, string, Required) — the template id,
 *   e.g. aldar-flight-booking-confirmation. Unknown id → 404
 *   TEMPLATE_NOT_FOUND. Body (full content replacement): - name (body,
 *   string, Yes) — template display name. Unique within (partner, vertical)
 *   → else 409 DUPLICATE_NAME. - subject (body, string, No) — email subject
 *   line; may contain {{...}} variables. - greeting (body, string, No) —
 *   greeting line. - body (body, string, No) — email body content. - ctaText
 *   (body, string, No) — call-to-action button label. -
 *
 * Notes: 200 OK. Response fields: id, vertical, name, subject, greeting, body,
 *   ctaText, ctaUrl, footerText, configured. FULL CONTENT REPLACEMENT (PUT)
 *   — omitted optional fields are not merged. Saving ALWAYS sets
 *   configured=true. vertical is NOT settable here (it is returned but not
 *   in the request body). ERRORS: 404 TEMPLATE_NOT_FOUND for an unknown
 *   templateId; 409 DUPLICATE_NAME on a name collision within (partner,
 *   vertical); 409 CONFLICT if the row is soft-deleted (must be restored
 *   first). ENUM — vertical (response): flight | hotel | activity. Response
 *   example is marked Derived in the spec.
 */
export const partnerBrandingEmailTemplatesUpdate = (body = {}) => callSpec("partnerBrandingEmailTemplatesUpdate", body, 'partner');

/**
 * §30.8  Delete Email Template
 *   FE      POST /app/v1/partner/branding/email-templates/delete
 *   engine  DELETE /api/v1/partners/{partnerId}/branding/email-templates/{templateId}   [stubbed]
 *
 * The spec carries templateId in the path; on the POST hop they travel
 * in the body.
 *
 * Request: Path: - partnerId (path, string, Required) — the partner / tenant id,
 *   e.g. aldar. - templateId (path, string, Required) — the template id.
 *   Unknown id → 404 TEMPLATE_NOT_FOUND. No query or body parameters.
 *
 * Notes: 200 OK with the soft-delete envelope: id, deleted (always true),
 *   deletedAt (ISO-8601, null on a live row), deletedBy, restorable. ⚠️
 *   CHANGED FROM SOURCE CONTRACT: the source returns 204 No Content with an
 *   empty body; under the soft-delete policy this returns 200 OK with the
 *   envelope above. The row is not removed from storage. SOFT-DELETE POLICY:
 *   idempotent (repeat delete returns 200 with the original deletedAt, no
 *   re-stamp); a direct GET on a soft-deleted id returns 410
 *   RESOURCE_DELETED, not 404; list endpoints omit it unless
 *   includeDeleted=true; PUT/PATCH on a soft-deleted id returns 409 CONF
 */
export const partnerBrandingEmailTemplatesDelete = (body = {}) => callSpec("partnerBrandingEmailTemplatesDelete", body, 'partner');

/**
 * §30.9  Get Document Section Catalog
 *   FE      POST /app/v1/partner/branding/document-sections/list
 *   engine  GET /api/v1/partners/{partnerId}/branding/document-sections   [stubbed]
 *
 * Request: Path: partnerId (path, string, Required) — the partner / tenant id, e.g.
 *   aldar. No query, header or body parameters.
 *
 * Notes: ⚠️ PROPOSED SHAPE / UNSPECIFIED WIRE FORMAT: the spec's Response
 *   Specification lists a single unnamed root object and states "The exact
 *   wire shape is not specified in the source." The example is marked
 *   Derived — document types and section keys taken from the shared "Section
 *   catalog" table. Do not treat the top-level keys as guaranteed. Returns
 *   which section toggle keys apply to each document type, for clients that
 *   need the catalog dynamically instead of hard-coding it. DOCUMENT TYPES:
 *   E-Ticket, Boarding Pass, Invoice, Hotel Voucher, Activity Voucher,
 *   Itinerary. SECTION KEYS by type — E-Ticket:
 */
export const partnerBrandingDocumentSectionsList = (body = {}) => callSpec("partnerBrandingDocumentSectionsList", body, 'partner');
